import React, { useEffect, useRef, useState } from "react";
import "../Styles/PhotographUploader.css";
import { delete_client_photo } from "../api/serverWrapper";
import { getUploadValidationError, UPLOAD_LIMITS } from "../utils/uploadLimits";

const EditPhotographUploader = ({
  client,
  images,
  setImages,
  setProfileImages,
  accessOnRequest,
  setAccessOnRequest,
  mainIndex,
  setMainIndex,
  otherMediaFiles = [],
}) => {
  const fileInputRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (imageFiles.length !== files.length) {
      alert("Please select image files only.");
      return;
    }

    const validationError = getUploadValidationError({
      photoFiles: [...images.map((image) => image.file), ...imageFiles],
      photoCount: images.length + imageFiles.length,
      otherFiles: otherMediaFiles,
    });
    if (validationError) {
      alert(validationError);
      return;
    }

    const readers = imageFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) =>
          resolve({
            file: file,
            preview: e.target.result,
          });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((newImages) => {
      setImages((prev) => {
        const combined = [...prev, ...newImages];
        if (prev.length === 0 && newImages.length > 0) {
          setCurrentIndex(0);
        }
        return combined;
      });
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async (item, index) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (currentIndex >= updated.length) {
        setCurrentIndex(Math.max(updated.length - 1, 0));
      }
      return updated;
    });

    // If server-side image, call delete API
    if (!item.file) {
      const actualUrl = item.source?.uri
        ? item.source.uri.split("?")[0]
        : item.url?.split("?")[0] || item.preview?.split("?")[0];

      if (actualUrl) {
        await delete_client_photo({
          UserId: client.User.UserId,
          imageUrl: actualUrl,
        });
      }
    }
  };

  useEffect(() => {
    const newUploadFiles = images
      .filter((img) => img.file instanceof File)
      .map((img) => img.file);
    setProfileImages(newUploadFiles);
  }, [images, setProfileImages]);
  useEffect(() => {
    if (client?.User?.WedLukDocs?.[0]?.ProfileImages?.length) {
      const imageUrls = client.User.WedLukDocs[0].ProfileImages;
      const mainUrl = client?.User?.ProfilePhotoUrl;

      const prepared = imageUrls.map((url) => ({
        file: null,
        preview: url,
      }));

      setImages(prepared);

      // ✅ FIND MAIN IMAGE INDEX
      if (mainUrl) {
        const index = imageUrls.findIndex(
          (url) => url.split("?")[0] === mainUrl.split("?")[0],
        );

        if (index !== -1) {
          setMainIndex(index);
        }
      }
    }
  }, [client]);

  const getImageSrc = (img) => {
    if (img.preview) return img.preview;
    if (img.url) return img.url;
    if (img.source?.uri) return img.source.uri;
    return "";
  };

  const handleSetMain = () => {
    setMainIndex(currentIndex);
  };
  return (
    <div className="PhotographUploader-container">
      <h2 className="PhotographUploader-title">Photographs</h2>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
        hidden
      />

      {images.length === 0 ? (
        <div
          className="PhotographUploader-drop-area"
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={onUploadClick}
        >
          <div className="PhotographUploader-upload-icon">
            <img
              src={require("../assests/images/upload-image.png")}
              alt="upload"
            />
          </div>
          <p className="PhotographUploader-upload-text">Upload / Drag images</p>
          <p className="PhotographUploader-subtext">
            Recommended image size: 500 × 700 px. Up to 10 photos, 3 MB each.
          </p>
        </div>
      ) : (
        <div className="PhotographUploader-gallery">
          <div className="PhotographUploader-main-view">
            <div className="PhotographUploader-main-view2">
              <button
                className="PhotographUploader-nav-button"
                onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
                disabled={currentIndex === 0}
              >
                ‹
              </button>

              <img
                src={getImageSrc(images[currentIndex])}
                alt="Main"
                className="PhotographUploader-main-image"
              />

              <button
                className="PhotographUploader-nav-button"
                onClick={() =>
                  setCurrentIndex((prev) =>
                    Math.min(prev + 1, images.length - 1),
                  )
                }
                disabled={currentIndex === images.length - 1}
              >
                ›
              </button>
            </div>

            {/* Bottom Bar */}
            <div className="profile-bottom-bar">
              <div className="profile-dots">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`dot ${i === currentIndex ? "active" : ""}`}
                  />
                ))}
              </div>

              <div className="d-flex gap-3">
                <input
                  type="checkbox"
                  checked={mainIndex === currentIndex}
                  onChange={handleSetMain}
                />
                <span className="set-main-text">Set as main profile</span>
                <span className="profile-count">
                  {currentIndex + 1}/{images.length}
                </span>
              </div>
            </div>
          </div>

          <div className="PhotographUploader-thumbnails">
            {images.map((img, index) => (
              <div key={index} className="PhotographUploader-thumbnail-wrapper">
                <img
                  src={getImageSrc(img)}
                  alt={`Thumbnail ${index}`}
                  className={`PhotographUploader-thumbnail ${
                    index === currentIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentIndex(index)}
                />
                <button
                  className="PhotographUploader-remove-button"
                  onClick={() => handleDelete(img, index)}
                >
                  ×
                </button>
              </div>
            ))}
            <div
              className="PhotographUploader-add-thumbnail"
              onClick={onUploadClick}
              title={`Add photos (up to ${UPLOAD_LIMITS.photoCount}, 3 MB each)`}
            >
              ＋
            </div>
          </div>
        </div>
      )}

      <div
        className="PhotographUploader-note d-flex gap-4"
        style={{ cursor: "pointer" }}
      >
        <input
          type="checkbox"
          id="private-images"
          checked={!accessOnRequest}
          onChange={(e) => setAccessOnRequest(!e.target.checked)}
        />
        <label htmlFor="private-images">
          <strong>Private images</strong> – Can be accessed only after your
          approval
        </label>
      </div>
    </div>
  );
};

export default EditPhotographUploader;
